import { FC, ReactNode, isValidElement, useContext } from 'react';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import AccountingTableContext from './AccountingTableContext';
import { AccountingRowProps } from './types';

const CellTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'cellFontSize',
})<{
  cellFontSize?: number | string;
}>(({ cellFontSize }) => ({
  ...(cellFontSize !== undefined && {
    '&&': {
      fontSize:
        typeof cellFontSize === 'number'
          ? `${cellFontSize}px !important`
          : `${cellFontSize} !important`,
    },
  }),
}));

const StyledCell = styled(TableCell, {
  shouldForwardProp: (prop) =>
    prop !== 'paddingY' &&
    prop !== 'paddingLeft' &&
    prop !== 'backgroundColor' &&
    prop !== 'fontWeight' &&
    prop !== 'hasLeftBorder' &&
    prop !== 'centered',
})<{
  paddingY: number;
  paddingLeft?: number;
  backgroundColor?: string;
  fontWeight?: number | string;
  hasLeftBorder: boolean;
  centered: boolean;
}>(
  ({
    theme,
    paddingY,
    paddingLeft,
    backgroundColor,
    fontWeight,
    hasLeftBorder,
    centered,
  }) => ({
    paddingTop: paddingY,
    paddingBottom: paddingY,
    paddingLeft: paddingLeft ?? theme.spacing(theme.spacingSizes.sm),
    paddingRight: theme.spacing(theme.spacingSizes.sm),
    fontWeight: fontWeight ?? theme.typography.h6.fontWeight,
    color: theme.palette.text.primary,
    backgroundColor: backgroundColor ?? 'transparent',
    whiteSpace: 'nowrap',
    ...(hasLeftBorder && {
      borderLeft: `1px solid ${theme.palette.grey[300]}`,
    }),
    ...(centered && {
      textAlign: 'center',
    }),
  }),
);

const AccountingRow: FC<AccountingRowProps> = ({
  cells,
  singleCell = false,
  centered = false,
  paddingY = 8,
  indentation = 16,
  color,
  backgroundColor,
  fontSize,
  fontWeight,
  cellLeftBorders = false,
}) => {
  const { columnCount } = useContext(AccountingTableContext);

  const getCellKey = (() => {
    const seen = new Map<string, number>();

    return (cell: ReactNode) => {
      const base =
        typeof cell === 'string' || typeof cell === 'number'
          ? `text:${String(cell)}`
          : isValidElement(cell)
          ? `el:${String(cell.key ?? cell.type)}`
          : `node:${typeof cell}`;

      const occurrence = seen.get(base) ?? 0;
      seen.set(base, occurrence + 1);
      return `${base}:${occurrence}`;
    };
  })();

  if (singleCell) {
    return (
      <TableRow>
        <StyledCell
          colSpan={columnCount}
          paddingY={paddingY}
          paddingLeft={indentation}
          backgroundColor={backgroundColor}
          fontWeight={fontWeight}
          hasLeftBorder={false}
          centered={centered}
        >
          {cells.map((cell) =>
            typeof cell === 'string' || typeof cell === 'number' ? (
              <CellTypography
                key={getCellKey(cell)}
                variant="h6"
                cellFontSize={fontSize}
                fontWeight="inherit"
                color={color}
              >
                {cell}
              </CellTypography>
            ) : (
              cell
            ),
          )}
        </StyledCell>
      </TableRow>
    );
  }

  return (
    <TableRow>
      {cells.map((cell, index) => (
        <StyledCell
          key={getCellKey(cell)}
          paddingY={paddingY}
          paddingLeft={index === 0 ? indentation : undefined}
          backgroundColor={backgroundColor}
          fontWeight={fontWeight}
          hasLeftBorder={
            typeof cellLeftBorders === 'boolean'
              ? cellLeftBorders
              : cellLeftBorders.includes(index)
          }
          centered={centered}
        >
          {typeof cell === 'string' || typeof cell === 'number' ? (
            <CellTypography
              variant="h6"
              cellFontSize={fontSize}
              fontWeight="inherit"
              color={color}
            >
              {cell}
            </CellTypography>
          ) : (
            cell
          )}
        </StyledCell>
      ))}
    </TableRow>
  );
};

export default AccountingRow;
